import { useStaffList } from "./useStaffList";
import { clientFormsEmail } from "./clientFormsEmail";
import { DateTime } from "luxon";
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import FullCalendar from "@fullcalendar/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import Modal from "react-modal";
import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  addDoc,
  getDocs,
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useAuth } from "../context/AuthContext"; // Import AuthContext hook
import "./Scheduler.css";
import SchedulerControls from "./SchedulerControls";
import { getBannerInfo } from "./bannerInfo";
import MobileAgenda from "./MobileAgenda";
import AppointmentSection, { sectionHasInformation } from "./AppointmentSection";
import { generatePDF } from "../utils/pdfExport";
import { generateAppointmentPDF } from "../utils/detailsPDFExport";
import { generateOrderFormNotesPDF } from "../utils/orderFormNotesExport";
import { query, orderBy } from "firebase/firestore";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";       // default theme

// Define a constant for summary default value
const SUMMARY_DEFAULT = "";
// Persist this marker in color; only real CSS colors reach FullCalendar.
const STRIPED_RED = "striped-red";
const STRIPED_RED_BACKGROUND =
  "repeating-linear-gradient(135deg, #b71c1c 0px, #b71c1c 8px, #d84343 8px, #d84343 16px)";

// Logs an activity to Firestore
export const logActivity = async (user, action, appointmentId, changedFields = {}) => {
  try {
    if (!user || !action || !appointmentId) {
      console.error("❌ Invalid log entry: Missing required fields.");
      return;
    }
    const appointmentRef = doc(db, "appointments", appointmentId);
    const appointmentSnapshot = await getDoc(appointmentRef);
    const appointmentData = appointmentSnapshot.exists() ? appointmentSnapshot.data() : {};
    const clientName = appointmentData.client || "Unknown Client";
    const appointmentDate = appointmentData.appointmentDate || "Not Set";
    const logEntry = {
      timestamp: serverTimestamp(),
      user: user,
      action: action,
      appointmentId: appointmentId,
      clientName: clientName,
      appointmentDate: appointmentDate,
      changedFields: changedFields,
      isDeleted: action === "Deleted",
    };

    await addDoc(collection(db, "activity_logs"), logEntry);
    console.log("✅ Activity logged successfully!", logEntry);
  } catch (error) {
    console.error("❌ Error logging activity:", error);
  }
};

// Saves an appointment's history before updating.
export const saveAppointmentHistory = async (appointmentId, user) => {
  try {
    const appointmentRef = doc(db, "appointments", appointmentId);
    const oldSnapshot = await getDoc(appointmentRef);
    if (oldSnapshot.exists()) {
      const oldData = oldSnapshot.data();
      const historyRef = collection(db, "appointments", appointmentId, "history");
      await addDoc(historyRef, {
        timestamp: serverTimestamp(),
        user: user,
        oldData: oldData,
      });
      console.log("✅ Appointment history saved:", oldData);
    }
  } catch (error) {
    console.error("❌ Error saving appointment history:", error);
  }
};

// Helper functions for calendar cell calculations
function getAssignedStaffForDate(appointments, dateISO) {
  const targetDay = DateTime.fromISO(dateISO).startOf("day");
  const dailyAppointments = appointments.filter((appt) => {
    if (!appt.appointmentDate) return false;
    const apptDay = DateTime.fromISO(appt.appointmentDate).startOf("day");
    return apptDay.hasSame(targetDay, "day");
  });
  const assignedSet = new Set();
  dailyAppointments.forEach((appt) => {
    if (Array.isArray(appt.photographers)) {
      appt.photographers.forEach((nick) => assignedSet.add(nick));
    }
  });
  return assignedSet;
}

function isLeadOrAnyRole(staff) {
  if (!staff.role) return false;
  const role = staff.role.toLowerCase();
  return role === "any job" || role === "small lead";
}

function isSecondShooter(staff) {
  if (!staff.role) return false;
  return staff.role.toLowerCase() === "2nd shooter";
}

// Main Scheduler component
const Scheduler = () => {
  const [mobileView, setMobileView] = useState("agenda");
  // Component states
  const [appointments, setAppointments] = useState([]);
  const [tipsVersion, setTipsVersion] = useState(0);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookedPhotographers, setBookedPhotographers] = useState([]);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableAppointment, setEditableAppointment] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFullscreen,setEditFullscreen]=useState(false);
  const [detailsFullscreen,setDetailsFullscreen]=useState(false);
  useEffect(()=>{if(!isEditModalOpen)setEditFullscreen(false);},[isEditModalOpen]);
  useEffect(()=>{if(!isModalOpen)setDetailsFullscreen(false);},[isModalOpen]);
  const { currentUser, isAdmin, isSuperAdmin } = useAuth();
  const staffList = useStaffList();

  const [selectedStaff, setSelectedStaff] = useState([]);
  const routerLocation = useLocation();
  
  const [exportFilter, setExportFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedField, setSelectedField] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const calendarRef = useRef(null);
  const [selectedStaffForDayOff, setSelectedStaffForDayOff] = useState(null);
  const [dayOffDate, setDayOffDate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);
  const colorOptions = [
    { value: "#33b5ff", label: "Blue" },
    { value: "#ff5733", label: "Red" },
    { value: STRIPED_RED, label: "Striped Red" },
    { value: "#357a38", label: "Green" },
    { value: "#dedede", label: "Grey" },
    { value: "#000000", label: "Black" },
    { value: "#800080", label: "Purple" },  // New
    { value: "#FFA500", label: "Orange" },  // New
    { value: "#FFD700", label: "Yellow" },  // New
    { value: "#505050", label: "Dark Grey" },  // New
    { value: "#ff77ff", label: "Pink" },  // New
    { value: "#FFFFFF", label: "White" },  // New
  ];
  const detailsStyle = selectedAppointment?.color === "#000000" 
    ? { textDecoration: "line-through" } 
    : {};

  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);

// ==== Day Notes state & helpers ====
const [dayNotes, setDayNotes] = useState(new Map()); // key: 'YYYY-MM-DD' -> array of notes
const [isDayNotesOpen, setIsDayNotesOpen] = useState(false);
const [activeNotesDate, setActiveNotesDate] = useState(null); // 'YYYY-MM-DD'
const newNoteRef = useRef(null);
const openDayNotes = (isoDayStr) => { setActiveNotesDate(isoDayStr); setIsDayNotesOpen(true); };

// date key helpers
const dayKeyFromDate = (d) => DateTime.fromJSDate(d).toISODate();
const dayKeyFromISO = (iso) => DateTime.fromISO(iso).toISODate();

// Tiny sticky-note icon with optional compact mode
  const NoteIcon = ({ count, compact = false }) => {
   // Don't render anything if there are no notes
   

   const size = compact ? 12 : 14;
   return (
     <div
       style={{
         display: "inline-flex",
        alignItems: "center",
        gap: 4,
         pointerEvents: "none", // icon shouldn't steal hover events from the button
       }}
     >
       <svg
         width={size}
         height={size}
         viewBox="0 0 24 24"
         fill="#f1c40f"
         stroke="#333"
         strokeWidth="1.2"
       >
         <path d="M14 2H6a2 2 0 0 0-2 2v16l4-4h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" />
       </svg>
        {/* Only show number badge when there are notes */}
      {count > 0 && (
        <span
          style={{
            fontSize: compact ? 9 : 10,
            background: "#333",
            color: "#fff",
            padding: "0 6px",
            borderRadius: 999,
            lineHeight: "16px",
            minWidth: compact ? 16 : 18,
            textAlign: "center",
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
};

// simple id generator (avoids extra deps/crypto warnings)
const makeNoteId = () => `n_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Append a note to a day (creates/merges the doc) */
const addDayNote = async (isoDayStr, text) => {
  if (!isoDayStr || !text || !text.trim()) return;
  const auth = getAuth();
  const userEmail = auth.currentUser?.email || "unknown";

  const note = {
    id: makeNoteId(),
    text: text.trim(),
    user: userEmail,
    ts: DateTime.now().toMillis(),
  };

  const ref = doc(db, "day_notes", isoDayStr);
  const current = dayNotes.get(isoDayStr) || [];
  const updated = [...current, note];

 await setDoc(ref, { date: isoDayStr, notes: updated }, { merge: true });
};

/** Delete a note by id from a given day */
const deleteDayNote = async (isoDayStr, noteId) => {
  if (!isoDayStr || !noteId) return;
  const ref = doc(db, "day_notes", isoDayStr);
  const current = dayNotes.get(isoDayStr) || [];
  const updated = current.filter((n) => n.id !== noteId);

  await setDoc(ref, { date: isoDayStr, notes: updated }, { merge: true });
};

  // New state for toggling extra info in calendar cells
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const toggleExtraInfo = () => setShowExtraInfo((prev) => !prev);

  // Returns weekday string from ISO date
  const getWeekdayString = (isoDate) => {
    if (!isoDate) return null;
    const dateObj = DateTime.fromISO(isoDate);
    return dateObj.toFormat("cccc");
  };

  // COLLAPSIBLE FILTERS START

  // COLLAPSIBLE FILTERS END

  // toggle between showing only today’s appts vs. all
  const [showToday, setShowToday] = useState(false);

  // tippy hover popups
const [hoverTipsEnabled, setHoverTipsEnabled] = useState(false);

const escapeHtml = (s = "") =>
  String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const fmtDT = (iso) =>
  iso
    ? DateTime.fromISO(iso).toFormat("MM-dd-yyyy h:mm a")
    : "";

const buildTooltipHTML = (ev) => {
  const x = ev.extendedProps || {};
  const rows = [
    ["Client", ev.title || x.client],
    ["Start", fmtDT(ev.startStr || x.appointmentDate)],
    ["End",   fmtDT(ev.endStr   || x.endDate)],
    ["Photogs", Array.isArray(x.photographers) ? x.photographers.join(", ") : x.photographers],
    ["Summary", x.summary],
    ["Equip",   x.extraEquipment],
    ["Notes",   x.notes],
  ].filter(([,v]) => v && String(v).trim());

  return `
    <div style="min-width:240px;max-width:400px">
      <div style="font-weight:600;margin-bottom:6px">Appointment</div>
      <table style="border-collapse:collapse;width:100%;font-size:12px">
        <tbody>
          ${rows.map(([k,v]) => `
            <tr>
              <td style="padding:2px 6px;white-space:nowrap;font-weight:600;vertical-align:top">${escapeHtml(k)}:</td>
              <td style="padding:2px 6px;vertical-align:top">${escapeHtml(String(v))}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
};

// Build Day Notes tooltip (updates live on show)
const buildDayNotesTooltipHTML = (isoDate) => {
  const notes = dayNotes.get(isoDate) || [];
  if (!notes.length) return "<em>No day notes</em>";

  const truncate = (s, n = 160) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
  const items = notes.slice(0, 5).map((n) => {
    const when = n.ts ? DateTime.fromMillis(n.ts).toFormat("MM-dd-yyyy h:mm a") : "";
    return `
      <li style="margin:6px 0">
        <div style="white-space:pre-wrap">${escapeHtml(truncate(n.text))}</div>
        <div style="color:#888;font-size:11px">${escapeHtml(n.user || "unknown")}${when ? " · " + escapeHtml(when) : ""}</div>
      </li>`;
  }).join("");

  const more = notes.length > 5
    ? `<div style="margin-top:6px;color:#888;font-size:12px">+${notes.length - 5} more…</div>`
    : "";

  return `
    <div style="min-width:220px;max-width:360px">
      <div style="font-weight:600;margin-bottom:4px">${escapeHtml(isoDate)} — Day Notes</div>
      <ul style="padding-left:16px;margin:0">${items}</ul>
      ${more}
    </div>
  `;
};


  // Effects
  useEffect(() => {
    console.log("Editable Appointment Updated:", editableAppointment);
  }, [editableAppointment]);

  useEffect(() => {
    console.log("Edit Mode Changed:", isEditMode);
  }, [isEditMode]);

  // NEW: Delay data fetching until currentUser is set
  useEffect(() => {
    if (!currentUser) {
      console.log("Waiting for authentication...");
      return;
    }
    // Now that the user is authenticated, fetch appointments
    fetchAppointments();
    // No need to call checkAdminStatus() here
  }, [currentUser]);

  useEffect(() => {
    filterAppointments();
  }, [searchTerm, selectedField, startDate, endDate, selectedColors]);

  useEffect(() => {
    console.log("Appointments Updated in State:", appointments);
  }, [appointments]);

  useEffect(() => {
    if (calendarRef.current && selectedDay) {
      calendarRef.current.getApi().gotoDate(selectedDay);
    }
  }, [selectedDay]);

  useEffect(() => {
    if (exportFilter === "week") {
      // Compute week boundaries based on today (or another reference date if needed)
      const today = DateTime.now();
      const startOfWeek = today.startOf("week"); // adjust if your week starts on a different day
      const endOfWeek = today.endOf("week");
      setExportStartDate(startOfWeek);
      setExportEndDate(endOfWeek);
    } else if (exportFilter === "month") {
      const today = DateTime.now();
      const startOfMonth = today.startOf("month");
      const endOfMonth = today.endOf("month");
      setExportStartDate(startOfMonth);
      setExportEndDate(endOfMonth);
    } else if (exportFilter === "all") {
      // For "all", we clear the export range so that no date filtering is applied during export
      setExportStartDate(null);
      setExportEndDate(null);
    }
  }, [exportFilter]);

  useEffect(() => {
    if (exportStartDate && exportEndDate) {
      // Optionally, navigate the calendar to the start date:
      if (calendarRef.current) {
        calendarRef.current.getApi().gotoDate(exportStartDate.toJSDate());
      }
    }
  }, [exportStartDate, exportEndDate]);

  // Track the visible calendar range (month view etc.)
const [viewStart, setViewStart] = useState(DateTime.now().startOf("month").startOf("week"));
const [viewEnd, setViewEnd] = useState(DateTime.now().endOf("month").endOf("week"));

// FullCalendar calls this whenever view changes (month/week nav)
const handleDatesSet = (arg) => {
  const start = DateTime.fromJSDate(arg.start);
  const endExclusive = DateTime.fromJSDate(arg.end); // FullCalendar end is exclusive
  setViewStart(start.startOf("day"));
  setViewEnd(endExclusive.minus({ days: 1 }).endOf("day"));
};

// Live subscription to day_notes in (or filtered to) the visible range
useEffect(() => {
  if (!viewStart || !viewEnd) return;
  const startKey = viewStart.toISODate(); // 'YYYY-MM-DD'
  const endKey = viewEnd.toISODate();

  // Simple approach: orderBy + client-side filter (no index needed)
  const qRef = query(collection(db, "day_notes"), orderBy("date"));

  // If you prefer server-side filtering (faster on huge collections), use:
  // const qRef = query(
  //   collection(db, "day_notes"),
  //   where("date", ">=", startKey),
  //   where("date", "<=", endKey),
  //   orderBy("date")
  // );

  const unsub = onSnapshot(qRef, (snap) => {
    const map = new Map();
    snap.docs.forEach((d) => {
      const data = d.data();
      if (data?.date >= startKey && data?.date <= endKey) {
        map.set(data.date, Array.isArray(data.notes) ? data.notes : []);
      }
    });
    setDayNotes(map);
  });

  return () => unsub();
}, [viewStart, viewEnd]);


  // Use an effect to check for appointmentId in location.state on component mount
  useEffect(() => {
    if (routerLocation.state && routerLocation.state.appointmentId) {
      const appointmentId = routerLocation.state.appointmentId;
      // Option 1: If you already have a listener that fetches all appointments,
      // you can simply set the selected appointment from the appointments state.
      // Option 2: Or, fetch the appointment details directly:
      const fetchAppointmentDetail = async () => {
        try {
          const docRef = doc(db, "appointments", appointmentId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
  const docData = docSnap.data();

  // Build the complete appointment object for the Details modal
  const appointmentData = {
    id: docSnap.id,
    ...docData,
    summary: docData.summary || SUMMARY_DEFAULT,

    // Add the start and end values used by the calendar and Details modal
    start: docData.appointmentDate
      ? DateTime.fromISO(docData.appointmentDate).toJSDate()
      : null,

    end: docData.endDate
      ? DateTime.fromISO(docData.endDate).toJSDate()
      : null,

    photographers: Array.isArray(docData.photographers)
      ? docData.photographers
      : docData.photographers
        ? [docData.photographers]
        : [],
  };

  setSelectedAppointment(appointmentData);
  setIsModalOpen(true);
}
        } catch (error) {
          console.error("Error fetching appointment detail:", error);
        }
      };
      fetchAppointmentDetail();
    }
  }, [routerLocation.state]);

  // Fetch appointments from Firestore
  const fetchAppointments = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "appointments"));
      const data = querySnapshot.docs.map((doc) => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          summary: docData.summary || SUMMARY_DEFAULT,  // NEW: add summary
          start: docData.appointmentDate ? DateTime.fromISO(docData.appointmentDate).toJSDate() : null,
          end: docData.endDate ? DateTime.fromISO(docData.endDate).toJSDate() : new Date(DateTime.fromISO(docData.appointmentDate).toJSDate().getTime() + 60 * 60 * 1000),
          photographers: Array.isArray(docData.photographers) ? docData.photographers : docData.photographers ? [docData.photographers] : [],
        };
      });
      console.log("✅ Fetched Appointments:", data);
      setAppointments(data);
    } catch (error) {
      console.error("❌ Error fetching appointments:", error);
    }
  };

  // Handle appointment click
  const handleAppointmentClick = (info) => {
    console.log("Appointment Clicked:", info.event);
    setSelectedAppointment({
      ...info.event.extendedProps,
      start: info.event.start,
      end: info.event.end,
    });
    setIsModalOpen(true);
  };

  // Real-time appointments listener
  useEffect(() => {
    const q = collection(db, "appointments");
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const events = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        start: DateTime.fromISO(doc.data().appointmentDate).toJSDate(),
        end: doc.data().endDate ? DateTime.fromISO(doc.data().endDate).toJSDate() : null,
      }));
      setAppointments(events);
    });
    return () => unsubscribe();
  }, []);

  // Refresh calendar
  const refreshCalendar = async () => {
    const querySnapshot = await getDocs(collection(db, "appointments"));
    const events = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      start: new Date(doc.data().appointmentDate),
      end: doc.data().endDate
        ? new Date(doc.data().endDate)
        : new Date(new Date(doc.data().appointmentDate).getTime() + 60 * 60 * 1000),
    }));
    setAppointments(events);
  };

  // Handle event drop (moving appointments)
const handleEventDrop = async (info) => {
  if (!isAdmin) return;

  const newStart = info.event.start;
  const newEnd =
    info.event.end || new Date(newStart.getTime() + 60 * 60 * 1000);

  const confirmMove = window.confirm(
    `Are you sure you want to move this appointment to ${
      newStart.toISOString().split("T")[0]
    }?`
  );

  if (!confirmMove) {
    info.revert();
    return;
  }

  try {
    const appointmentRef = doc(db, "appointments", info.event.id);

    // Save appointment history before moving
    await saveAppointmentHistory(
      info.event.id,
      currentUser?.email || "Unknown User"
    );

    // Update appointment in Firestore
    await updateDoc(appointmentRef, {
      appointmentDate: newStart.toISOString(),
      endDate: newEnd.toISOString(),
    });

    // Record the move in the Activity Log
    await logActivity(
      currentUser?.email || "Unknown User",
      "Moved Appointment",
      info.event.id,
      {
        originalDate: info.oldEvent.start.toISOString(),
        newDate: newStart.toISOString(),
      }
    );

    console.log("✅ Appointment moved and activity logged!");

    await refreshCalendar();
  } catch (error) {
    console.error("❌ Error updating appointment:", error);
    alert("Failed to update appointment.");
    info.revert();
  }
};

// tippy refresh
useEffect(() => {
  setTipsVersion((v) => v + 1);
}, [appointments]);

  // Helper: Convert appointment date to weekday (for editing, etc.)
  const dayOfWeek = getWeekdayString(editableAppointment?.appointmentDate);

  // Handle deleting an appointment
const handleDeleteAppointment = async (appointmentId) => {
  if (!isSuperAdmin) {
    alert("You do not have permission to delete appointments.");
    return;
  }

  const confirmDelete = window.confirm(
    "Are you sure you want to delete this appointment?"
  );

  if (!confirmDelete) return;

  try {
    const appointmentRef = doc(db, "appointments", appointmentId);
    const appointmentSnapshot = await getDoc(appointmentRef);

    if (!appointmentSnapshot.exists()) {
      alert("This appointment no longer exists.");
      return;
    }

    const appointment = appointmentSnapshot.data();
    const user = currentUser?.email || "Unknown User";

    // Save deletion and activity record together
    const batch = writeBatch(db);

    const logRef = doc(collection(db, "activity_logs"));

    batch.set(logRef, {
      timestamp: serverTimestamp(),
      user,
      action: "Deleted",
      appointmentId,
      clientName: appointment.client || "Unknown Client",
      appointmentDate: appointment.appointmentDate || "",
      changedFields: {
        deletedAppointmentId: appointmentId,
        deletedDate: appointment.appointmentDate || "",
      },
      isDeleted: true,
    });

    batch.delete(appointmentRef);

    await batch.commit();

    // Update the calendar
    setAppointments((prev) =>
      prev.filter((appt) => appt.id !== appointmentId)
    );

    setSelectedAppointment(null);
setIsModalOpen(false);

    alert("Appointment deleted successfully.");

  } catch (error) {
    console.error("Error deleting appointment:", error);
    alert("Failed to delete appointment.");
  }
};

// Copy appointment
const handleCopyAppointment = async (appointment, selectedDate) => {
  if (!selectedDate) {
    alert("Please select a date for the copied appointment.");
    return;
  }

  try {
    console.log("🛠️ Copying Appointment:", appointment);
    console.log("📅 Selected Date:", selectedDate);

    const timeZone = "America/Detroit";

    // Read the original appointment's start and end times
    const originalStart = DateTime.fromISO(appointment.appointmentDate)
      .setZone(timeZone);

    const originalEnd = appointment.endDate
      ? DateTime.fromISO(appointment.endDate).setZone(timeZone)
      : originalStart.plus({ hours: 1 });

    // Get the date selected for the copied appointment
    const targetDate = selectedDate.setZone(timeZone);

    // Preserve the original appointment's starting time
    const copiedStart = targetDate.set({
      hour: originalStart.hour,
      minute: originalStart.minute,
      second: 0,
      millisecond: 0,
    });

    // Preserve the original appointment's duration
    const duration = originalEnd.diff(originalStart);

    const copiedEnd = copiedStart.plus(duration);

    // Convert dates to UTC for Firestore
    const newStart = copiedStart.toUTC().toISO();
    const newEnd = copiedEnd.toUTC().toISO();

    // Look up the latest Client ID from the clients collection
let currentClientId = appointment.clientId || "";

if (appointment.client) {
  const clientsSnapshot = await getDocs(collection(db, "clients"));
  console.log("🔍 Appointment client name:", appointment.client);
console.log("🔍 Original Client ID:", appointment.clientId);

console.table(
  clientsSnapshot.docs.map((clientDoc) => ({
    name: clientDoc.data().name,
    clientIdField: clientDoc.data().clientId,
    idField: clientDoc.data().id,
    firestoreDocId: clientDoc.id,
  }))
);

  const matchingClients = clientsSnapshot.docs.filter((clientDoc) => {
    const clientName = clientDoc.data().name || "";

    return (
      clientName.trim().toLowerCase() ===
      appointment.client.trim().toLowerCase()
    );
  });

  if (matchingClients.length === 1) {
console.log(
  "🔍 MATCHED CLIENT RECORD:",
  matchingClients[0].data()
);

    currentClientId = matchingClients[0].data().clientID || "";

    console.log("✅ Current Client ID found:", currentClientId);
  } else if (matchingClients.length === 0) {
    alert(
      `Client "${appointment.client}" was not found in the client list. The original Client ID will be kept.`
    );
  } else {
    alert(
      `Multiple clients match "${appointment.client}". The original Client ID will be kept.`
    );
  }
}

    // Create the copied appointment
const newAppointment = {
  ...appointment,
  bannerInfo: getBannerInfo(appointment),
  photographers: ["Unassigned"],
  appointmentDate: newStart,
  endDate: newEnd,
  startTime: newStart,
  endTime: newEnd,
  clientID: currentClientId, // Use the latest Client ID
  color: "#800080", // Automatically make copied appointments purple
};

    // Remove temporary calendar fields
    delete newAppointment.id;
    delete newAppointment.start;
    delete newAppointment.end;

    // Save the copied appointment to Firestore
    const docRef = await addDoc(
      collection(db, "appointments"),
      newAppointment
    );

    // Record the copy in the Activity Log
    await logActivity(
      currentUser?.email || "Unknown User",
      "Copied Appointment",
      docRef.id,
      {
        originalAppointmentId: appointment.id,
        originalDate: appointment.appointmentDate,
        newDate: newStart,
      }
    );

    console.log(
      "✅ Copied appointment saved in Firestore with id:",
      docRef.id
    );

    // Build the copied appointment for the details modal
    const copiedAppointment = {
      ...newAppointment,
      id: docRef.id,
      start: copiedStart.toJSDate(),
      end: copiedEnd.toJSDate(),
    };

    // Open the copied appointment details
    setSelectedAppointment(copiedAppointment);
    setIsModalOpen(true);

  } catch (error) {
    console.error("❌ Error copying appointment:", error);
    alert(`Failed to copy appointment: ${error.message}`);
  }
};


  // Fetch appointment history
  const fetchAppointmentHistory = async (appointmentId) => {
    try {
      const historyRef = collection(db, "appointments", appointmentId, "history");
      const q = query(historyRef, orderBy("timestamp", "desc"));
      const querySnapshot = await getDocs(q);
      const historyData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp
          ? doc.data().timestamp.toDate().toLocaleString("en-US", { timeZone: "America/New_York" })
          : "",
      }));
      return historyData;
    } catch (error) {
      console.error("❌ Error fetching appointment history:", error);
      return [];
    }
  };

  // Enable edit mode
  const enableEditMode = async () => {
    console.log("Editing Mode Activated");
    console.log("Selected Appointment Data:", selectedAppointment);
    const historyData = await fetchAppointmentHistory(selectedAppointment.id);
    setAppointmentHistory(historyData);
    const appointmentDate = DateTime.fromISO(selectedAppointment?.appointmentDate).toISODate();
    const photographersBooked = appointments
      .filter(
        (appt) =>
          DateTime.fromISO(appt.appointmentDate).toISODate() === appointmentDate &&
          appt.id !== selectedAppointment?.id
      )
      .flatMap((appt) => appt.photographers || []);
    setEditableAppointment({
      ...selectedAppointment,
      startTime: selectedAppointment?.appointmentDate
        ? DateTime.fromISO(selectedAppointment.appointmentDate).toFormat("HH:mm")
        : "",
      endTime: selectedAppointment?.endDate
        ? DateTime.fromISO(selectedAppointment.endDate).toFormat("HH:mm")
        : "",
      alternateShipTo: selectedAppointment?.alternateShipTo || "",
      photographers: Array.isArray(selectedAppointment?.photographers)
        ? selectedAppointment.photographers
        : [],
      color: selectedAppointment?.color || "",
      orderFormNotes: selectedAppointment?.orderFormNotes || "",
      bannerInfo: getBannerInfo(selectedAppointment),
      summary: selectedAppointment?.summary || SUMMARY_DEFAULT,  // NEW: include summary in edit state
    });
    setBookedPhotographers(photographersBooked);
    setIsEditModalOpen(true);
    setIsModalOpen(false);
  };

  // Handle field changes in edit form
  const handleFieldChange = (field, value) => {
    setEditableAppointment((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Save changes to Firestore
  const handleSaveChanges = async () => {
    if (!editableAppointment?.id) return;
    const auth = getAuth();
    const user = auth.currentUser ? auth.currentUser.email : "Unknown User";




  
    // Validate photographers' availability as in your current code...
    const chosenPhotographers = editableAppointment.photographers || [];
    const dayOfWeek = getWeekdayString(editableAppointment.appointmentDate);
    const isoDate = DateTime.fromISO(editableAppointment.appointmentDate).toISODate();
    const unavailableNames = chosenPhotographers.filter((nick) => {
      const staff = staffList.find((s) => s.nickname === nick);
      return staff && Array.isArray(staff.availability) && !staff.availability.includes(dayOfWeek);
    });
    if (unavailableNames.length > 0) {
      const proceed = window.confirm(
        `Warning: ${unavailableNames.join(", ")} not available on ${dayOfWeek}. Continue anyway?`
      );
      if (!proceed) return;
    }
    const dayOffStaff = chosenPhotographers.filter((nick) => {
      const staff = staffList.find((s) => s.nickname === nick);
      return staff?.dayOffRequests?.includes(isoDate);
    });
    if (dayOffStaff.length > 0) {
      const proceed = window.confirm(
        `${dayOffStaff.join(", ")} has requested the day off. Continue anyway?`
      );
      if (!proceed) return;
    }
  
    try {
      const appointmentRef = doc(db, "appointments", editableAppointment.id);
  
      // Get the date part from the existing appointmentDate (which is a full ISO string)
      const datePart = DateTime.fromISO(editableAppointment.appointmentDate).toISODate(); // e.g. "2025-02-26"
      
      // Parse the new startTime input ("HH:mm") and merge with datePart
      const [startHour, startMinute] = editableAppointment.startTime.split(":").map(Number);
      const fullStartDateTime = DateTime.fromISO(datePart)
        .set({ hour: startHour, minute: startMinute })
        .toUTC()
        .toISO();
  
      // Do the same for endTime:
      const [endHour, endMinute] = editableAppointment.endTime.split(":").map(Number);
      const fullEndDateTime = DateTime.fromISO(datePart)
        .set({ hour: endHour, minute: endMinute })
        .toUTC()
        .toISO();
  
      // Build the updated appointment object
      const updatedPhotographers = Array.isArray(editableAppointment.photographers)
        ? editableAppointment.photographers
        : [];
      const updatedAppointment = {
        ...editableAppointment,
        // Update the appointmentDate and endDate with the full ISO date/time strings
        appointmentDate: fullStartDateTime,
        endDate: fullEndDateTime,
        startTime: editableAppointment.startTime,
        endTime: editableAppointment.endTime,
        photographers: updatedPhotographers,
        color: editableAppointment?.color || "#ff5733",
        orderFormNotes: editableAppointment?.orderFormNotes || "",
        bannerInfo: getBannerInfo(editableAppointment),
        alternateShipTo: editableAppointment?.alternateShipTo || "",
        summary: editableAppointment?.summary || SUMMARY_DEFAULT,  // NEW: include summary here
      };
      // Remove temporary calendar fields before saving
delete updatedAppointment.id;
delete updatedAppointment.start;
delete updatedAppointment.end;
  
      await saveAppointmentHistory(editableAppointment.id, user);
  
      // Identify which appointment fields changed
const changedFields = {};

Object.keys(updatedAppointment).forEach((key) => {
  const oldValue = selectedAppointment[key];
  const newValue = updatedAppointment[key];

  if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
    changedFields[key] = `${oldValue ?? ""} → ${newValue ?? ""}`;
  }
});

// Save the updated appointment to Firestore FIRST
await updateDoc(appointmentRef, updatedAppointment);

// Record the update only after the save succeeds
if (Object.keys(changedFields).length > 0) {
  await logActivity(
    user,
    "Updated Appointment",
    editableAppointment.id,
    changedFields
  );
}

      console.log("✅ Appointment updated successfully!", updatedAppointment);
     setIsEditModalOpen(false);
setEditableAppointment(null);

// Build the complete appointment object for the Details modal
const refreshedAppointment = {
  ...updatedAppointment,
  id: editableAppointment.id,
  start: DateTime.fromISO(fullStartDateTime).toJSDate(),
  end: DateTime.fromISO(fullEndDateTime).toJSDate(),
};

// Update the selected appointment with complete calendar data
setSelectedAppointment(refreshedAppointment);

// Reopen the Details modal
setIsModalOpen(true);

// Refresh the calendar
await fetchAppointments();
    } catch (error) {
      console.error("❌ Error updating appointment:", error);
      alert("Failed to update appointment.");
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditableAppointment(null);
    setIsModalOpen(true);
  };

  const todayISO = DateTime.now().toISODate();
  const filteredAppointments = appointments.filter((appt) => {
    if (showToday) {
     const apptDay = DateTime.fromISO(appt.appointmentDate).toISODate();
     if (apptDay !== todayISO) return false;
   }
    const apptDate = DateTime.fromISO(appt.appointmentDate);
    const appointmentDateStr = apptDate.toISODate();
  
    const now = DateTime.now();
    const startOfWeek = now.startOf("week").toISODate();
    const endOfWeek = now.endOf("week").toISODate();
    const startOfMonth = now.startOf("month").toISODate();
    const endOfMonth = now.endOf("month").toISODate();
  
    const matchesStaff = 
      selectedStaff.length === 0 ||
      (Array.isArray(appt.photographers) &&
        appt.photographers.some((p) => selectedStaff.includes(p)));
  
    const matchesDate =
      exportFilter === "week"
        ? appointmentDateStr >= startOfWeek && appointmentDateStr <= endOfWeek
        : exportFilter === "month"
        ? appointmentDateStr >= startOfMonth && appointmentDateStr <= endOfMonth
        : exportFilter === "range"
        ? ((!exportStartDate || apptDate >= exportStartDate) && (!exportEndDate || apptDate <= exportEndDate))
        : true;
  
    const matchesColor =
      selectedColors.length === 0 ||
      selectedColors.some((colorOption) => colorOption.value === appt.color);
  
    return matchesStaff && matchesDate && matchesColor;
  });

  // Full calendar search filter
  const filterAppointments = () => {
    if (!searchTerm && !startDate && !endDate) {
      setSearchResults([]);
      return;
    }
    const filtered = appointments.filter((appt) => {
      const appointmentDate = DateTime.fromISO(appt.appointmentDate);
      const isWithinDateRange =
        (!startDate || appointmentDate >= startDate) &&
        (!endDate || appointmentDate <= endDate);
      const lowerCaseSearch = searchTerm.toLowerCase();
      const matchesSearchTerm =
        (!searchTerm ||
        (((selectedField === "all" || selectedField === "client") &&
          appt.client?.toLowerCase().includes(lowerCaseSearch)) ||
          ((selectedField === "all" || selectedField === "photographer") &&
            (Array.isArray(appt.photographers)
              ? appt.photographers.some((p) => p.toLowerCase().includes(lowerCaseSearch))
              : false)) ||
          ((selectedField === "all" || selectedField === "notes") &&
            appt.notes?.toLowerCase().includes(lowerCaseSearch))));
      const matchesOtherFields = selectedField === "all" && [
        appt.summary, appt.address, appt.fieldBuildingLocation, appt.extraEquipment,
        appt.bellNotes, getBannerInfo(appt),
        appt.productionNotes, appt.orderFormNotes, appt.clientID,
      ].some(value => String(value || "").toLowerCase().includes(lowerCaseSearch));
      return isWithinDateRange && (matchesSearchTerm || matchesOtherFields);
    });
          // 🔥 NEW: sort newest → oldest
      const sorted = filtered.sort((a, b) =>
        DateTime.fromISO(b.appointmentDate).toMillis() -
        DateTime.fromISO(a.appointmentDate).toMillis()
      );

setSearchResults(sorted);
  };

  // tippy functions
const fcEventDidMount = (arg) => {
  // Always clean any previous instance on this element
  if (arg.el._tippy) arg.el._tippy.destroy();
  if (!hoverTipsEnabled) return;

  // Helper that always pulls the latest event props
  const getHtml = () => buildTooltipHTML(arg.event);

  tippy(arg.el, {
    content: getHtml(),               // initial content
    allowHTML: true,
    interactive: true,
    appendTo: document.body,
    placement: "top",
    delay: [100, 50],
    maxWidth: 420,

    // 🔑 Refresh content right before showing (uses *current* event data)
    onShow(instance) {
      instance.setContent(getHtml());
    },
    // Also refresh anytime the trigger fires (hover/focus)
    onTrigger(instance) {
      instance.setContent(getHtml());
    },
  });
};


const fcEventWillUnmount = (arg) => {
  if (arg.el._tippy) arg.el._tippy.destroy();
};


  // Move to appointment (for calendar search results)
  const moveToAppointment = (appointment) => {
    if (!appointment || !appointment.appointmentDate) return;
    const appointmentDate = DateTime.fromISO(appointment.appointmentDate);
    if (calendarRef.current) {
      console.log("📅 Moving Calendar to:", appointmentDate.toFormat("MM-dd-yyyy"));
      calendarRef.current.getApi().gotoDate(appointmentDate.toJSDate());
    } else {
      console.error("❌ calendarRef is not available.");
    }
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  //handle send text handlesendtext//
  const handleSendText = async () => {
    if (!selectedAppointment || !selectedAppointment.photographers) {
      alert("No photographers assigned to this appointment.");
      return;
    }

    const phoneNumbers = [];
    for (const photographerName of selectedAppointment.photographers) {
      const staffQuery = collection(db, "staff");
      const staffSnapshot = await getDocs(staffQuery);
      
      staffSnapshot.docs.forEach((doc) => {
        const staffData = doc.data();
        if (staffData.nickname === photographerName && staffData.phone) {
          phoneNumbers.push(staffData.phone);
        }
      });
    }

    if (phoneNumbers.length === 0) {
      alert("No valid phone numbers found for assigned photographers.");
      return;
    }

    const appointmentDate = DateTime.fromISO(selectedAppointment.appointmentDate).toFormat("MM-dd-yyyy");
    const clientName = selectedAppointment.client || "Unknown Client";

    // Format startTime so it shows in 12-hour format
    const formattedStartTime = selectedAppointment.startTime
      ? DateTime.fromISO(selectedAppointment.startTime).toFormat("h:mm a")
      : "N/A";

    // ✅ NEW: include extra equipment
    const extraEquipment =
      selectedAppointment.extraEquipment && selectedAppointment.extraEquipment.trim()
        ? selectedAppointment.extraEquipment
        : "None";  

    //sms send functions//
    try {
      const response = await fetch("https://api-zgou5a2hta-uc.a.run.app/api/sms/send-sms", { // ✅ Added /api prefix
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumbers, appointmentDate, clientName, startTime: formattedStartTime, extraEquipment,  }),
      });

      const data = await response.json();
      if (response.ok) {
        alert("SMS notifications sent successfully!");
      } else {
        alert("Failed to send SMS: " + data.error);
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      alert("Error sending SMS.");
    }
  };

  //handle pdf export calendar current view
  const handleExportPDF = () => {
    generatePDF(filteredAppointments);
  };

  // renderDayCell for FullCalendar day cells (uses showExtraInfo toggle)
  const renderDayCell = (arg) => {
  const isoDate = DateTime.fromJSDate(arg.date).toISODate();
  const dayOfWeek = DateTime.fromJSDate(arg.date).toFormat("EEEE");

  const allStaff = staffList;
  const nonOfficeStaff = staffList.filter(
    (staff) => staff.role.toLowerCase() !== "office"
  );

  const staffAvailableToday = nonOfficeStaff.filter((staff) => {
    const normallyAvailable = staff.availability.includes(dayOfWeek);
    const hasDayOff = staff.dayOffRequests?.includes(isoDate);
    return normallyAvailable && !hasDayOff;
  });

  const staffDayOff = allStaff.filter((staff) =>
    staff.dayOffRequests?.includes(isoDate)
  );

  const assignedStaffIDs = getAssignedStaffForDate(appointments, isoDate);
  const assignedStaff = staffAvailableToday.filter((staff) =>
    assignedStaffIDs.has(staff.nickname)
  );
  const overallFreeCount = staffAvailableToday.length - assignedStaff.length;

  const anyJobAvailable = staffAvailableToday.filter(
    (s) => s.role.toLowerCase() === "any job"
  );
  const anyJobAssigned = assignedStaff.filter(
    (s) => s.role.toLowerCase() === "any job"
  );
  const anyJobFree = anyJobAvailable.length - anyJobAssigned.length;

  const smallLeadAvailable = staffAvailableToday.filter(
    (s) => s.role.toLowerCase() === "small lead"
  );
  const smallLeadAssigned = assignedStaff.filter(
    (s) => s.role.toLowerCase() === "small lead"
  );
  const smallLeadFree = smallLeadAvailable.length - smallLeadAssigned.length;

  const secondShooterAvailable = staffAvailableToday.filter(isSecondShooter);
  const secondShooterAssigned = assignedStaff.filter(isSecondShooter);
  const secondShooterFree =
    secondShooterAvailable.length - secondShooterAssigned.length;

  return (
     <div
     style={{
       position: "relative",
       paddingLeft: showExtraInfo ? "120px" : "0",
       // Reserve space on the right for the note icon when extra info is OFF
       paddingRight: showExtraInfo ? 0 : 28,
      minHeight: 22, // keeps the header row from feeling cramped
     }}
   >
      {/* Sticky note pinned to the cell's top-right, NOT in the flex row */}
      <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();
    openDayNotes(isoDate);
  }}
  title="Day Notes"
  aria-label={`Open day notes for ${isoDate}`}
  style={{
    position: "absolute",
    top: 3,
    right: 3,
    background: "transparent",
    border: "none",
    padding: 0,
    lineHeight: 0,
    cursor: "pointer",
    zIndex: 3,
    opacity: showExtraInfo ? 1 : 0.75, // lighter in compact mode
  }}
  ref={(el) => {
    if (!el) return;
    // clean any previous tooltip on this element
    if (el._tippy) el._tippy.destroy();
    tippy(el, {
      content: buildDayNotesTooltipHTML(isoDate),
      allowHTML: true,
      interactive: false,
      appendTo: document.body,
      placement: "top",
      delay: [100, 50],
      maxWidth: 360,
      // ensure fresh content every time (handles live updates)
      onShow(instance) {
        instance.setContent(buildDayNotesTooltipHTML(isoDate));
      },
    });
  }}
>
  <NoteIcon count={(dayNotes.get(isoDate) || []).length} compact={!showExtraInfo} />
</button>


      {showExtraInfo && (
        <>
          {/* Left column of “Off” names stays absolute */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {staffDayOff.map((staff) => (
              <div key={staff.nickname} style={{ fontSize: "0.54rem", color: "red" }}>
                {staff.nickname} Off
              </div>
            ))}
          </div>

          {/* MAIN column: stack everything vertically (no flex row!) */}
          <div>
            <div style={{ fontWeight: "bold" }}>{arg.dayNumberText}</div>
            <div style={{ fontSize: "0.75rem", color: "#666" }}>
              {`${overallFreeCount} free / ${staffAvailableToday.length} avail`}
            </div>
            <div style={{ fontSize: "0.7rem", color: "#666" }}>
              Any Job: {anyJobFree} / {anyJobAvailable.length}
            </div>
            <div style={{ fontSize: "0.7rem", color: "#666" }}>
              Small Lead: {smallLeadFree} / {smallLeadAvailable.length}
            </div>
            <div style={{ fontSize: "0.7rem", color: "#666" }}>
              2nd Shooter: {secondShooterFree} / {secondShooterAvailable.length}
            </div>
          </div>
        </>
      )}

      {!showExtraInfo && (
  <div>
    <div
      style={{
        fontWeight: "bold",
        textAlign: "left",  // push number to the left
        paddingRight: 8,   // extra space so it never sits under the icon
      }}
    >
      {arg.dayNumberText}
    </div>
  </div>
)}
    </div>
  );
};

  
  return (
    <div className="scheduler-page">
      {/* Render any filters, search inputs, and modals above the calendar */}
      {isSuperAdmin ? (
        <p>✅ You are a Super Admin</p>
      ) : isAdmin ? (
        <p>✅ You are an Admin</p>
      ) : null}

      <SchedulerControls
        onSearch={filterAppointments}
        {...{staffList, colorOptions, selectedStaff, setSelectedStaff, selectedColors, setSelectedColors,
          showToday, setShowToday, exportFilter, setExportFilter, exportStartDate, setExportStartDate,
          exportEndDate, setExportEndDate, searchTerm, setSearchTerm, selectedField, setSelectedField,
          startDate, setStartDate, endDate, setEndDate, searchResults, moveToAppointment,
          showExtraInfo, toggleExtraInfo, hoverTipsEnabled, setHoverTipsEnabled, handleExportPDF}}
        onExportOrderForms={() => generateOrderFormNotesPDF(filteredAppointments, exportStartDate, exportEndDate)}
      />

      <style>{
        `.fc .fc-event.appointment-striped-red {
          background-image: ${STRIPED_RED_BACKGROUND};
          border-color: #b71c1c;
        }`
      }</style>
      <div className="mobile-schedule-views" role="group" aria-label="Schedule view">
        <button aria-pressed={mobileView === "agenda"} onClick={() => setMobileView("agenda")}>Agenda</button>
        <button aria-pressed={mobileView === "calendar"} onClick={() => {
          setMobileView("calendar");
          requestAnimationFrame(() => calendarRef.current?.getApi().updateSize());
        }}>Calendar</button>
      </div>
      {mobileView === "agenda" && <MobileAgenda appointments={filteredAppointments}
        start={viewStart} end={viewEnd} onOpen={moveToAppointment}
        onPrevious={() => calendarRef.current?.getApi().prev()}
        onNext={() => calendarRef.current?.getApi().next()}
        onToday={() => calendarRef.current?.getApi().today()} />}
      <div className={mobileView === "agenda" ? "mobile-calendar-hidden" : ""}>
      <FullCalendar
        key={`${hoverTipsEnabled ? "tips-on" : "tips-off"}-${tipsVersion}`}
        ref={calendarRef}
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
        initialView="dayGridMonth"
        editable={isAdmin}
        eventDrop={handleEventDrop}
        headerToolbar={{
          left: "prevYear,prev today next,nextYear",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}

         buttonText={{
    today: "Today",
    month: "Month",
    week: "Week",
    day: "Day",
    prevYear: "<<",
    nextYear: ">>"
  }}


        datesSet={handleDatesSet}
        views={{
          dayGridMonth: { buttonText: "Month" },
          timeGridWeek: { buttonText: "Week" },
          timeGridDay: { buttonText: "Day" },
        }}
        slotMinTime="07:00:00"
        slotMaxTime="23:00:00"
        dayCellContent={(arg) => renderDayCell(arg)}
        eventClassNames={({ event }) =>
          event.extendedProps.color === STRIPED_RED ? ["appointment-striped-red"] : []
        }
        eventContent={(eventInfo) => {
  const startTime = eventInfo.event.start
    ? DateTime.fromJSDate(eventInfo.event.start).toFormat("h:mm ")
    : "";

  const bgColor = eventInfo.event.backgroundColor || "";
  const textColor =
    bgColor.toLowerCase() === "#dedede" ||
    bgColor.toLowerCase() === "#ffffff"
      ? "#000000"
      : "#ffffff";

  const detailsStyle = {
    borderLeft: "1px solid white",
    paddingLeft: "5px",
    width: "100%",
    boxSizing: "border-box",
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    lineHeight: "1.2",
    ...(eventInfo.event.extendedProps.color === "#000000" && {
      textDecoration: "line-through",
    }),
  };

  return (
    <div
      style={{
        backgroundColor: eventInfo.event.backgroundColor,
        ...(eventInfo.event.extendedProps.color === STRIPED_RED && {
          backgroundImage: STRIPED_RED_BACKGROUND,
        }),
        color: textColor,
        padding: "5px",
        borderRadius: "5px",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        whiteSpace: "normal",
      }}
    >
      <div style={detailsStyle}>
        <strong>{startTime}</strong>
        <span>{eventInfo.event.title}</span>
      </div>
    </div>
  );
}}

        eventMouseEnter={(info) => {
          if (info.el?._tippy) {
            info.el._tippy.setContent(buildTooltipHTML(info.event));
          }
        }}

        events={filteredAppointments.map((appt) => {
          let eventColor = appt.color === STRIPED_RED
            ? "#b71c1c"
            : appt.color || "#dedede";
          if (!eventColor.startsWith("#")) {
            eventColor = "#" + eventColor;
          }
          return {
            id: appt.id,
            title: appt.client,
            start: appt.start,
            end: appt.end,
            extendedProps: { ...appt },
            backgroundColor: eventColor,
            textColor: "#ffffff",
          };
        })}
        eventClick={handleAppointmentClick}
        eventDidMount={fcEventDidMount}
        eventWillUnmount={fcEventWillUnmount}
      />
      </div>
      {/* Edit Appointment Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className={`reactModal scheduler-dialog scheduler-appointment-dialog${editFullscreen ? " scheduler-dialog-fullscreen" : ""}`}
        overlayClassName="reactModalOverlay"
      >
        <div
          className="modal-container"
        >
          <div
            className="modal-header"
            style={{ padding: "10px", backgroundColor: "#f1f1f1", cursor: "grab" }}
          >
            <h2>Edit Appointment</h2>
            <button type="button" className="scheduler-fullscreen-toggle" aria-pressed={editFullscreen} aria-label={editFullscreen ? "Restore edit window size" : "Expand edit window to full screen"} onClick={()=>setEditFullscreen(value=>!value)}>{editFullscreen ? "Restore" : "Full screen"}</button>
            <button className="scheduler-mobile-close" onClick={handleCancelEdit}>Close</button>
            
          </div>
          {editableAppointment && (
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "auto", display: "flex",
              flexDirection: "column",
              height: "85vh",}}>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                }}
              >
<div className="appointment-section-grid"><div className="appointment-section-wide"><AppointmentSection title="Schedule & location" key={(editableAppointment?.id || "draft") + "Schedule & location"} initiallyOpen={true}><p>
                  <strong>Client:</strong>
                  <input
                    type="text"
                    defaultValue={editableAppointment?.client || ""}
                    onBlur={(e) => handleFieldChange("client", e.target.value)}
                  />
                </p>
<div className="appointment-date-time-row"><p>
                  <strong>Date:</strong>
                  <DatePicker portalId="scheduler-datepicker-portal"
                    selected={
                      editableAppointment?.appointmentDate
                        ? DateTime.fromISO(editableAppointment.appointmentDate).toJSDate()
                        : null
                    }
                    onChange={(date) =>
                      setEditableAppointment((prev) => ({
                        ...prev,
                        appointmentDate: DateTime.fromJSDate(date).toUTC().toISO(),
                      }))
                    }
                    dateFormat="MM-dd-yyyy"
                  />
                </p>
<p>
                  <strong>Start Time:</strong>
                  <input
                    type="time"
                    value={editableAppointment?.startTime || ""}
                    onChange={(e) => handleFieldChange("startTime", e.target.value)}
                  />
                </p>
<p>
                  <strong>End Time:</strong>
                  <input
                    type="time"
                    value={editableAppointment?.endTime || ""}
                    onChange={(e) => handleFieldChange("endTime", e.target.value)}
                  />
                </p></div>
<p>
                  <strong>Address:</strong>
                  <textarea
                    defaultValue={editableAppointment?.address || ""}
                    onBlur={(e) => handleFieldChange("address", e.target.value)}
                    rows={3}
                    style={{ width: "100%" }}
                  />
                </p>
<p>
                  <strong>Field/Building Location:</strong>
                  <textarea
                    defaultValue={editableAppointment?.fieldBuildingLocation || ""}
                    onBlur={(e) => handleFieldChange("fieldBuildingLocation", e.target.value)}
                    rows={3}
                    style={{ width: "100%" }}
                  />
                </p>

</AppointmentSection></div>
<div className="appointment-section-wide"><AppointmentSection title="Crew" key={(editableAppointment?.id || "draft") + "Crew"} initiallyOpen={true}><p>
                  <strong>Photographers:</strong>
                  <Select
                    isMulti
                    options={staffList
                      .filter((staff) => staff.role && staff.role.toLowerCase() !== "office")
                      .sort((a, b) => a.nickname.localeCompare(b.nickname))
                      .map((staff) => {
                        const isAvailable =
                          dayOfWeek && Array.isArray(staff.availability)
                            ? staff.availability.includes(dayOfWeek)
                            : false;
                        const isoDate = DateTime.fromJSDate(
                          editableAppointment?.appointmentDate ? new Date(editableAppointment.appointmentDate) : new Date()
                        ).toISODate();
                        const isDayOff = staff.dayOffRequests?.includes(isoDate);
                        let label = staff.nickname;
                        if (!isAvailable) {
                          label += " (N/A)";
                        } else if (isDayOff) {
                          label += " (D/O)";
                        }
                        return {
                          value: staff.nickname,
                          label,
                          isBooked: bookedPhotographers.includes(staff.nickname),
                        };
                      })}
                    value={
                      Array.isArray(editableAppointment?.photographers)
                        ? editableAppointment.photographers
                            .slice()
                            .sort((a, b) => a.localeCompare(b))
                            .map((nickname) => ({
                              value: nickname,
                              label: nickname,
                            }))
                        : []
                    }
                    onChange={(selected) =>
                      setEditableAppointment((prev) => ({
                        ...prev,
                        photographers: selected.map((item) => item.value),
                      }))
                    }
                    styles={{
                      option: (base, { data }) => ({
                        ...base,
                        backgroundColor: data.isBooked ? "#ffcccc" : "white",
                        color: data.isBooked ? "red" : "black",
                      }),
                    }}
                  />
                </p>
<p>
                 <strong>Photographer Roles:</strong>
                 <textarea
                   defaultValue={editableAppointment?.bellNotes || ""}
                   onBlur={(e) => handleFieldChange("bellNotes", e.target.value)}
                   rows={3}
                   style={{ width: "100%" }}
                 />
               </p></AppointmentSection></div>
<AppointmentSection className="appointment-edit-paired" title="Job instructions" key={(editableAppointment?.id || "draft") + "Job instructions"} initiallyOpen={sectionHasInformation(editableAppointment,["summary","extraEquipment","notes"])} hasInformation={sectionHasInformation(editableAppointment,["summary","extraEquipment","notes"])}><p>
                  <strong>Summary:</strong>
                  <textarea
                    defaultValue={editableAppointment?.summary || ""}
                    onBlur={(e) => handleFieldChange("summary", e.target.value)}
                    rows={1}
                    style={{ width: "100%", height: "40px", minHeight: "40px" }}
                  />
                </p>
<p>
                  <strong>Extra Equipment:</strong>
                  <textarea
                    defaultValue={editableAppointment?.extraEquipment || ""}
                    onBlur={(e) => handleFieldChange("extraEquipment", e.target.value)}
                    rows={1}
                    style={{ width: "100%", height: "40px", minHeight: "40px" }}
                  />
                </p>
<p>
                  <strong>Notes & Schedule:</strong>
                  <textarea
                    defaultValue={editableAppointment?.notes || ""}
                    onBlur={(e) => handleFieldChange("notes", e.target.value)}
                    rows={8}
                    style={{ width: "100%", height: "500px" }}
                  />
                </p>

</AppointmentSection>
<AppointmentSection className="appointment-edit-paired appointment-edit-banner" title="Banner information" key={(editableAppointment?.id || "draft") + "Banner information"} initiallyOpen={Boolean(getBannerInfo(editableAppointment).trim())} hasInformation={Boolean(getBannerInfo(editableAppointment).trim())}><p><strong>Banner Info:</strong><textarea value={editableAppointment?.bannerInfo ?? ""} onChange={event=>handleFieldChange("bannerInfo",event.target.value)} rows={25} style={{height:"680px"}} /></p></AppointmentSection>
<AppointmentSection title="Production & order forms" key={(editableAppointment?.id || "draft") + "Production & order forms"} initiallyOpen={sectionHasInformation(editableAppointment,["productionNotes","orderFormNotes","clientID"])} hasInformation={sectionHasInformation(editableAppointment,["productionNotes","orderFormNotes","clientID"])}><p>
                  <strong>Production Notes:</strong>
                  <textarea
                    defaultValue={editableAppointment?.productionNotes || ""}
                    onBlur={(e) => handleFieldChange("productionNotes", e.target.value)}
                    style={{ width: "100%", height: "80px" }}
                  />
                </p>
<p>
                  <strong>Order Form Notes:</strong>
                  <textarea
                    defaultValue={editableAppointment?.orderFormNotes || ""}
                    onBlur={(e) => handleFieldChange("orderFormNotes", e.target.value)}
                  />
                </p>
<p>
                 <strong>Client ID:</strong>
                 <input
                   type="text"
                   value={editableAppointment?.clientID || ""}
                   onChange={(e) => handleFieldChange("clientID", e.target.value)}
                   style={{ width: "100%" }}
                 />
               </p></AppointmentSection>
<AppointmentSection title="Shipping & contacts" key={(editableAppointment?.id || "draft") + "Shipping & contacts"} initiallyOpen={sectionHasInformation(editableAppointment,["alternateShipTo","appointmentContacts"])} hasInformation={sectionHasInformation(editableAppointment,["alternateShipTo","appointmentContacts"])}><p>
                  <strong>Ship To:</strong>
                  <textarea
                    defaultValue={editableAppointment?.alternateShipTo || ""}
                    onBlur={(e) => handleFieldChange("alternateShipTo", e.target.value)}
                    rows={3}
                    style={{ width: "100%" }}
                  />
                </p>
<p>
                  <strong>Appointment Contacts:</strong>
                  <textarea
                    defaultValue={editableAppointment?.appointmentContacts || ""}
                    onBlur={(e) => handleFieldChange("appointmentContacts", e.target.value)}
                    style={{ width: "100%", height: "80px" }}
                  />
                </p></AppointmentSection></div>
</div>
              <div className="scheduler-dialog-actions appointment-edit-actions" style={{ marginTop: "10px", textAlign: "center" }}><div className="appointment-color-before-save"><p>
                  <strong>Appointment Color:</strong>
                  <select
                    value={editableAppointment?.color || "#dedede"}
                    onChange={(e) => handleFieldChange("color", e.target.value)}
                  >
                    <option value="#dedede">Grey</option>
                    <option value="#ff5733">Red</option>
                    <option value={STRIPED_RED}>Striped Red</option>
                    <option value="#357a38">Green</option>
                    <option value="#33b5ff">Blue</option>
                    <option value="#000000">Black</option>
                    <option value="#800080">Purple</option>
                    <option value="#FFA500">Orange</option>
                    <option value="#FFD700">Yellow</option>
                    <option value="#505050">Dark Grey</option>
                    <option value="#ff77ff">Pink</option>
                    <option value="#FFFFFF">White</option>
                  </select>
                </p></div>
                <button
                  onClick={handleSaveChanges}
                  style={{ backgroundColor: "green", color: "white", padding: "10px", marginRight: "10px", borderRadius: "5px" }}
                >
                  Save Changes
                </button>
                <button
                  onClick={handleCancelEdit}
                  style={{ backgroundColor: "gray", color: "white", padding: "10px", borderRadius: "5px" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
      {/* Appointment Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className={`reactModal scheduler-dialog scheduler-appointment-dialog${detailsFullscreen ? " scheduler-dialog-fullscreen" : ""}`}
        overlayClassName="reactModalOverlay"
      >
        <div
          className="modal-container"
        >
          <div
            className="modal-header"
            style={{
              padding: "10px",
              backgroundColor: "#f1f1f1",
              cursor: "grab",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2>Appointment Details</h2>
            <button type="button" className="scheduler-fullscreen-toggle" aria-pressed={detailsFullscreen} aria-label={detailsFullscreen ? "Restore details window size" : "Expand details window to full screen"} onClick={()=>setDetailsFullscreen(value=>!value)}>{detailsFullscreen ? "Restore" : "Full screen"}</button>
            <button aria-label="Close appointment details" onClick={() => setIsModalOpen(false)}>Close</button>
          </div>
          <div
            className="modal-content"
            style={{
              maxWidth: "1200px",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              height: "85vh",
            }}
          >
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                backgroundColor: "#fff",
              }}
            >
              {selectedAppointment && (
                <div style={detailsStyle}><h2>{selectedAppointment?.client || "No Client Name"}</h2>
<div className="appointment-section-grid"><div className="appointment-section-wide"><AppointmentSection title="Schedule & location" key={(selectedAppointment?.id || "draft") + "Schedule & location"} initiallyOpen={true}><div className="appointment-date-time-row"><p>
                    <strong>Date:</strong>{" "}
                    {selectedAppointment?.appointmentDate
                      ? DateTime.fromISO(selectedAppointment.appointmentDate).toFormat("cccc, MM-dd-yyyy")
                      : "Not Set"}
                  </p>
<p>
                    <strong>Start Time:</strong>{" "}
                    {selectedAppointment?.startTime
                      ? DateTime.fromISO(selectedAppointment.startTime).toFormat("hh:mm a")
                      : "Not Set"}
                  </p>
<p>
                    <strong>End Time:</strong>{" "}
                    {selectedAppointment?.end
                      ? DateTime.fromJSDate(selectedAppointment.end).toFormat("hh:mm a")
                      : "Not Set"}
                  </p></div>
<p>
                <strong>Address:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.address || "Not Set"}
                </span>
              </p>
<p>
                <strong>Field/Building Location:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.fieldBuildingLocation || "Not Specified"}
                </span>
              </p>
</AppointmentSection></div>
<div className="appointment-section-wide"><AppointmentSection title="Crew" key={(selectedAppointment?.id || "draft") + "Crew"} initiallyOpen={true}><p>
                <strong>Photographers:</strong>{" "}
                <span className="multiline-text">
                  {Array.isArray(selectedAppointment.photographers)
                    ? selectedAppointment.photographers.join(", ")
                    : selectedAppointment.photographers || "None"}
                </span>
              </p>
<p>
               <strong>Photographer Roles:</strong>{" "}
               <span className="multiline-text">
                 {selectedAppointment.bellNotes || "None"}
              </span>
             </p></AppointmentSection></div>
<AppointmentSection title="Job instructions" key={(selectedAppointment?.id || "draft") + "Job instructions"} initiallyOpen={sectionHasInformation(selectedAppointment,["summary","extraEquipment","notes"])} hasInformation={sectionHasInformation(selectedAppointment,["summary","extraEquipment","notes"])}><p>
                <strong>Summary:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.summary || "No Summary Provided"}
                </span>
              </p>
<p>
                <strong>Extra Equipment:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.extraEquipment || "None"}
                </span>
              </p>
<p>
                <strong>Notes & Schedule:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.notes || "None"}
                </span>
              </p>

</AppointmentSection>
<AppointmentSection title="Banner information" key={(selectedAppointment?.id || "draft") + "Banner information"} initiallyOpen={Boolean(getBannerInfo(selectedAppointment).trim())} hasInformation={Boolean(getBannerInfo(selectedAppointment).trim())}><p><strong>Banner Info:</strong><span className="multiline-text">{getBannerInfo(selectedAppointment)}</span></p></AppointmentSection>
<AppointmentSection title="Production & order forms" key={(selectedAppointment?.id || "draft") + "Production & order forms"} initiallyOpen={sectionHasInformation(selectedAppointment,["productionNotes","orderFormNotes","clientID"])} hasInformation={sectionHasInformation(selectedAppointment,["productionNotes","orderFormNotes","clientID"])}><p>
                <strong>Production Notes:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.productionNotes || ""}
                </span>
              </p>
<p>
                <strong>Order Form Notes:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.orderFormNotes || ""}
                </span>
              </p>
<p>
               <strong>Client ID:</strong>{" "}
               <span className="multiline-text">
                {selectedAppointment.clientID || "N/A"}
               </span>
             </p>{isAdmin && (<button className="appointment-forms-request" type="button" title="Open an email draft to pictures@vsnmichigan.com with the saved appointment details" onClick={()=>{if(selectedAppointment)window.location.href=clientFormsEmail(selectedAppointment,currentUser?.email).href;}} style={{backgroundColor:"#007bff",color:"white",borderRadius:"5px"}}>Request client forms</button>)}</AppointmentSection>
<AppointmentSection title="Shipping & contacts" key={(selectedAppointment?.id || "draft") + "Shipping & contacts"} initiallyOpen={sectionHasInformation(selectedAppointment,["alternateShipTo","appointmentContacts"])} hasInformation={sectionHasInformation(selectedAppointment,["alternateShipTo","appointmentContacts"])}><p>
                <strong>Ship To:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.alternateShipTo || ""}
                </span>
              </p>
<p>
                <strong>Appointment Contacts:</strong>{" "}
                <span className="multiline-text">
                  {selectedAppointment.appointmentContacts || ""}
                </span>
              </p></AppointmentSection></div>
</div>
              )}
            </div>
            <div className="scheduler-dialog-actions" style={{ marginTop: "10px", textAlign: "center" }}>
              {isSuperAdmin && (
                <button
                  onClick={() => handleDeleteAppointment(selectedAppointment.id)}
                  style={{ backgroundColor: "red", color: "white", padding: "8px", marginTop: "10px", marginRight: "10px", borderRadius: "5px" }}
                >
                  Delete
                </button>
              )}
              <button
  onClick={async () => {
    try {
      await generateAppointmentPDF(selectedAppointment);
    } catch (error) {
      console.error("Error exporting appointment PDF:", error);
      alert("Failed to export appointment PDF.");
    }
  }}
  style={{ backgroundColor: "blue", color: "white", padding: "8px", marginRight: "10px", borderRadius: "5px" }}
>
  Export
</button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setIsCopyMode(true)}
                    style={{ backgroundColor: "blue", color: "white", padding: "8px", marginRight: "10px", borderRadius: "5px" }}
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => enableEditMode()}
                    style={{ backgroundColor: "blue", color: "white", padding: "8px", borderRadius: "5px" }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleSendText}
                    style={{
                      backgroundColor: "#007bff",
                      color: "white",
                      padding: "8px",
                      marginTop: "10px",
                      marginLeft: "10px",
                      borderRadius: "5px",
                    }}
                  >
                    Text Photogs
                  </button>
                  
                </>
              )}
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setIsCopyMode(false);
                }}
                style={{ backgroundColor: "#ccc", color: "black", padding: "8px", marginTop: "10px", marginLeft: "10px", borderRadius: "5px" }}
              >
                Close
              </button>
              {isCopyMode && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                    width: "fit-content",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  <h3 style={{ marginBottom: "5px" }}>Select a Date for the Copy</h3>
                  <DatePicker portalId="scheduler-datepicker-portal"
                    selected={selectedDate ? new Date(selectedDate) : null}
                    onChange={(date) => setSelectedDate(DateTime.fromJSDate(date).toUTC())}
                    dateFormat="yyyy-MM-dd"
                  />
                  <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                    <button
                      onClick={() => {
                        handleCopyAppointment(selectedAppointment, selectedDate);
                        setIsCopyMode(false);
                      }}
                      style={{ backgroundColor: "green", color: "white", padding: "8px", borderRadius: "5px" }}
                    >
                      Confirm Copy
                    </button>
                    <button
                      onClick={() => setIsCopyMode(false)}
                      style={{ backgroundColor: "gray", color: "white", padding: "8px", borderRadius: "5px" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

<Modal
  isOpen={isDayNotesOpen}
  onRequestClose={() => setIsDayNotesOpen(false)}
  shouldCloseOnOverlayClick={true}
  ariaHideApp={false}
  className="reactModal scheduler-dialog"
  overlayClassName="reactModalOverlay"
>
  <div style={{ padding: 12, maxWidth: 700 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <h3 style={{ margin: 0 }}>Day Notes — {activeNotesDate || ""}</h3>
      <button onClick={() => setIsDayNotesOpen(false)}>Close</button>
    </div>

    <div style={{ marginTop: 12 }}>
      {(dayNotes.get(activeNotesDate) || []).length === 0 ? (
        <div style={{ color: "#666" }}>No notes yet.</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {(dayNotes.get(activeNotesDate) || []).map((n) => (
            <li key={n.id} style={{ padding: "8px 0", borderBottom: "1px solid #eee" }}>
              <div style={{ whiteSpace: "pre-wrap" }}>{n.text}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {n.user || "unknown"} · {n.ts ? DateTime.fromMillis(n.ts).toFormat("MM-dd-yyyy h:mm a") : ""}
              </div>
              {isAdmin && (
                <button style={{ marginTop: 6 }} onClick={() => deleteDayNote(activeNotesDate, n.id)}>
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>

    {isAdmin && (
      <div style={{ marginTop: 12 }}>
        + <textarea
   ref={newNoteRef}
   placeholder="Add a new note…"
   defaultValue=""
   rows={3}
   style={{ width: "100%" }}
   onKeyDown={(e) => {
     if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
       const v = newNoteRef.current?.value || "";
       addDayNote(activeNotesDate, v);
       if (newNoteRef.current) newNoteRef.current.value = "";
     }
   }}
 />
 <button
   style={{ marginTop: 8 }}
   onClick={() => {
     const v = newNoteRef.current?.value || "";
     addDayNote(activeNotesDate, v);
     if (newNoteRef.current) newNoteRef.current.value = "";
   }}
 >
   Add Note
 </button>
        <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
          Tip: Press Ctrl/Cmd + Enter to add quickly
        </div>
      </div>
    )}
  </div>
</Modal>


    </div>
  );
};

export default Scheduler;
